// One dispatch column in the Playground grid: what the queue reports for a
// single REAL client, one block per sent message.
//
// Two things this is NOT:
//
//  - It is not the eval matrix. A Playground message asserts nothing (the case
//    carries no expectations, so the server marks the grade `ungraded`), so the
//    column shows the response, its tool calls and a compact status — never a
//    pass/fail badge. Pass/fail stays where it means something, on
//    /evals/suite/:id/runs/:runId, one "Run detail" link away.
//
//  - It is not a plain block of app-styled text. mcpjam styled each host's chat
//    surface so a Claude column read as Claude and a ChatGPT column as ChatGPT;
//    that registry is still here, so the column wears its client's chrome —
//    palette, bubble family, brand mark, tool blocks — through
//    `ScenarioHostStyleProvider` + `getScenarioShellStyle`. The `direct` column
//    keeps the sphinx house style; it is rendered elsewhere by PlaygroundMain.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ExternalLink, Loader2 } from "lucide-react";
import { ToolCallPart } from "@sphinx/chat-ui";
import { cn } from "@/lib/utils";
import type { PlaygroundRun } from "@/data/playground";
import { targetDisplayName } from "@/data/targets";
import type { HostThemeMode } from "@/lib/client-styles";
import {
  getScenarioChatBackground,
  getScenarioHostLogo,
  getScenarioShellStyle,
} from "@/lib/scenario-client-style";
import { clientHostStyle } from "@/lib/playground-client-style";
import type { SphinxModel, TargetId } from "@/shared/sphinx";
import {
  PlaygroundClientModelPicker,
  modelLabel,
} from "./PlaygroundClientModelPicker";
import type { SphinxIteration } from "@/lib/sphinx-api";
import {
  serverRecordedToolCalls,
  serverRecordedToolNames,
  transcriptOf,
} from "@/components/evals/sphinx-run-detail";
import { mergeIteration } from "@/components/evals/sphinx-run-matrix";
import type { LiveIteration } from "@/components/evals/sphinx-run-stream-reducer";
import {
  ScenarioHostStyleProvider,
  ScenarioHostThemeProvider,
} from "@/contexts/scenario-client-style-context";
import { UserMessageBubble } from "@/components/chat-v2/thread/user-message-bubble";
import { MemoizedMarkdown } from "@/components/chat-v2/thread/memomized-markdown";
import { LoadingIndicatorContent } from "@/components/chat-v2/shared/loading-indicator-content";

interface PlaygroundRunColumnProps {
  target: TargetId;
  runs: PlaygroundRun[];
  themeMode?: HostThemeMode | null;
  /** Run the SSE tail is following, so live state is applied to the right block. */
  liveRunId?: string | null;
  /** Live per-iteration state from `GET /runs/:id/stream`, by iteration id. */
  liveIterations?: Record<string, LiveIteration>;
  /** This client's model for the next send (its own pick, not the lineup's). */
  model: SphinxModel | null;
  onModelChange: (model: SphinxModel) => void;
  /**
   * The session has started: the model is part of what this chat IS, so the
   * picker gives way to a static label. A new chat unlocks it again.
   */
  modelLocked?: boolean;
}

export function PlaygroundRunColumn({
  target,
  runs,
  themeMode,
  liveRunId = null,
  liveIterations,
  model,
  onModelChange,
  modelLocked = false,
}: PlaygroundRunColumnProps) {
  const name = targetDisplayName(target);
  const hostStyle = clientHostStyle(target);
  const theme: HostThemeMode = themeMode ?? "light";
  const logo = getScenarioHostLogo(hostStyle, undefined, theme);
  const background = getScenarioChatBackground(hostStyle, theme);
  return (
    <ScenarioHostStyleProvider value={hostStyle}>
      <ScenarioHostThemeProvider value={theme}>
        <div
          className={cn(
            "scenario-host-shell app-theme-scope flex min-h-0 flex-col overflow-hidden rounded-lg border border-border",
            theme === "dark" && "dark"
          )}
          data-testid={`playground-run-column-${target}`}
          data-host-style={hostStyle}
          data-thread-theme={theme}
          style={{
            ...getScenarioShellStyle(hostStyle, theme),
            backgroundColor: background,
          }}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
            {logo ? (
              <img
                src={logo}
                alt=""
                className="size-4 shrink-0 object-contain"
              />
            ) : null}
            <span className="truncate text-sm font-medium text-foreground">
              {name}
            </span>
            {modelLocked ? (
              <span
                className="ml-auto inline-flex max-w-[9rem] items-center rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                data-testid={`playground-client-model-${target}`}
                data-model={model ?? ""}
              >
                <span className="truncate">
                  {model ? modelLabel(model) : ""}
                </span>
              </span>
            ) : (
              <PlaygroundClientModelPicker
                target={target}
                model={model}
                onModelChange={onModelChange}
                className="ml-auto"
              />
            )}
          </div>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-3 text-sm">
            {runs.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Send a message to run it on {name}.
              </p>
            ) : (
              runs.map((entry) => (
                <RunBlock
                  key={entry.key}
                  entry={entry}
                  target={target}
                  liveIterations={
                    entry.runId && entry.runId === liveRunId
                      ? liveIterations
                      : undefined
                  }
                />
              ))
            )}
          </div>
        </div>
      </ScenarioHostThemeProvider>
    </ScenarioHostStyleProvider>
  );
}

function RunBlock({
  entry,
  target,
  liveIterations,
}: {
  entry: PlaygroundRun;
  target: TargetId;
  liveIterations?: Record<string, LiveIteration>;
}) {
  // Exactly one iteration per client here: a Playground dispatch names one
  // model per client, and an inline pick collapses that client's configured
  // `models` list to it (`createRunInTx`). An eval run is the one that fans a
  // client out over several models.
  const iteration =
    entry.run?.iterations.find((it) => it.target === target) ?? null;
  const live = iteration ? liveIterations?.[iteration.id] : undefined;
  return (
    <div className="space-y-3" data-testid="playground-run-block">
      <UserMessageBubble>
        <p className="whitespace-pre-wrap">{entry.prompt}</p>
      </UserMessageBubble>
      {entry.runId === null && entry.error ? (
        <p className="whitespace-pre-wrap rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          Dispatch failed: {entry.error}
        </p>
      ) : (
        <IterationResult entry={entry} iteration={iteration} live={live} />
      )}
    </div>
  );
}

/** Whole-number seconds between two ISO timestamps, or null. */
function elapsedSeconds(
  startedAt: string | null,
  finishedAt: string | null
): number | null {
  if (!startedAt || !finishedAt) return null;
  const ms = Date.parse(finishedAt) - Date.parse(startedAt);
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 1000) : null;
}

/**
 * The one status line a Playground column shows: "queued on ec2-mac-1",
 * "running", "grading", "done in 42s", "canceled". Deliberately not pass/fail
 * — see the file header.
 *
 * `status` and `graded` are the MERGED values: the stream can be ahead of the
 * 2s poll, and reading the row alone would leave a finished iteration saying
 * "grading" for another poll.
 */
function statusLine(
  iteration: SphinxIteration,
  status: string,
  graded: boolean,
  queuePosition?: number
): {
  text: string;
  busy: boolean;
} {
  const where = iteration.runnerName
    ? ` on ${iteration.runnerName}`
    : queuePosition
      ? ` (#${queuePosition})`
      : "";
  switch (status) {
    case "running":
      return { text: `running${where}`, busy: true };
    case "canceled":
      return { text: "canceled", busy: false };
    case "failed":
      return { text: "failed", busy: false };
    case "complete": {
      // `complete` without a grade is still being graded server-side.
      if (!graded) return { text: "grading", busy: true };
      const seconds = elapsedSeconds(iteration.startedAt, iteration.finishedAt);
      return {
        text: seconds === null ? "done" : `done in ${seconds}s`,
        busy: false,
      };
    }
    default:
      return { text: `queued${where}`, busy: true };
  }
}

/**
 * One row: a spinner at the text's line height, then the status, then the
 * "Run detail" link.
 *
 * The client's own thinking indicator never renders IN this row. Those are
 * message-slot widgets that hard-code their own box (Claude's mark is a 32px
 * square of absolutely-positioned strips, which ignored a `size-3.5` on the
 * wrapper and painted an orange asterisk straight over "running on
 * ec2-mac-1"; two of them draw their own "Thinking" label — David's
 * screenshot, 2026-08-22). They get a message slot of their own in
 * `IterationResult`, and while one is on screen `showSpinner: false` keeps
 * this row from spinning twice.
 */
function StatusRow({
  text,
  busy,
  showSpinner = true,
  children,
}: {
  text: string;
  busy: boolean;
  showSpinner?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span
        className="inline-flex min-w-0 items-center gap-2"
        data-testid="playground-run-status"
      >
        {busy && showSpinner ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
        ) : null}
        <span className="truncate">{text}</span>
      </span>
      {children}
    </div>
  );
}

const REVEAL_CHARS_PER_TICK = 6;
const REVEAL_TICK_MS = 16;

/**
 * Reveal response text at a readable pace. mac/CLI targets deliver the
 * finished transcript in one poll rather than a token stream, so without this
 * the answer snaps in all at once — unlike every streamed chat surface. Text
 * already on screen at mount (reopening a page with a finished run) shows
 * instantly; only text that APPEARS while the block is mounted animates.
 */
function useRevealedText(text: string): string {
  const animateRef = useRef(text.length === 0);
  const [visible, setVisible] = useState(animateRef.current ? 0 : text.length);
  useEffect(() => {
    if (!animateRef.current || visible >= text.length) return;
    const timer = setTimeout(
      () => setVisible((v) => Math.min(text.length, v + REVEAL_CHARS_PER_TICK)),
      REVEAL_TICK_MS
    );
    return () => clearTimeout(timer);
  }, [text, visible]);
  return animateRef.current ? text.slice(0, Math.min(visible, text.length)) : text;
}

/** The response body, markdown-rendered through the progressive reveal. */
function RevealedResponse({ text }: { text: string }) {
  const revealed = useRevealedText(text);
  return (
    <div
      className="text-sm text-foreground [&_p]:my-1"
      data-testid="playground-run-response"
    >
      <MemoizedMarkdown content={revealed} />
    </div>
  );
}

function IterationResult({
  entry,
  iteration,
  live,
}: {
  entry: PlaygroundRun;
  iteration: SphinxIteration | null;
  live?: LiveIteration;
}) {
  if (!iteration) {
    return (
      <StatusRow
        text={entry.error ? entry.error : "dispatching"}
        busy={!entry.error}
      />
    );
  }
  const {
    status: mergedStatus,
    grade,
    error,
  } = mergeIteration(iteration, live);
  const status = statusLine(
    iteration,
    mergedStatus,
    grade !== null,
    live?.queuePosition
  );
  const transcript = transcriptOf(iteration.clientTranscript);
  const turn = transcript?.turns[0] ?? null;
  // Until the finished transcript lands, show what the stream has written so
  // far (anthropic-api streams its answer as `text_delta`).
  const responseText = turn?.responseText || live?.text || "";
  // Tool-call precedence: for a scraped transcript, Yarn's own attributable
  // record (full args + results) beats the transcript's inferred names — the
  // desktop apps barely render tool use. An exact transcript is authoritative.
  // While running, the live stream fills in (the server tail marks its
  // entries `serverRecorded`).
  const serverCalls = serverRecordedToolCalls(iteration.serverTruth);
  const transcriptCalls = turn?.observedToolCalls ?? [];
  const liveCalls = live?.toolCalls ?? [];
  const useServerCalls =
    serverCalls.length > 0 &&
    (transcript?.exactness === "scraped" || transcriptCalls.length === 0);
  const toolCalls = useServerCalls
    ? serverCalls
    : transcriptCalls.length > 0
      ? transcriptCalls
      : liveCalls;
  const serverSourced =
    useServerCalls ||
    (toolCalls === liveCalls && liveCalls.some((c) => c.serverRecorded));
  const runDetailPath =
    entry.suiteId && entry.runId
      ? `/evals/suite/${encodeURIComponent(entry.suiteId)}/runs/${encodeURIComponent(entry.runId)}`
      : null;
  // The client's own thinking indicator, in a message slot of its own —
  // exactly where its host renders it — until the first response text lands.
  const showBrandIndicator = status.busy && !responseText;
  return (
    <div className="space-y-3">
      {toolCalls.length > 0 ? (
        <div className="space-y-2" data-testid="playground-run-tool-calls">
          {serverSourced ? (
            <div
              className="text-[10px] uppercase tracking-wide text-muted-foreground"
              data-testid="playground-run-server-sourced"
              title="This client's surface does not render tool use; these calls come from Yarn's own MCP logs for this run."
            >
              tool calls · from Yarn&apos;s server logs
            </div>
          ) : null}
          {toolCalls.map((call, index) => (
            <ToolCallPart
              key={`${call.name}-${index}`}
              // Label the serving connector: a multi-connector run calls the
              // same tool once per org, and unlabeled they read as duplicates.
              toolName={
                (call as { server?: string }).server
                  ? `${call.name} · ${(call as { server?: string }).server}`
                  : call.name
              }
              toolState={call.isError ? "output-error" : "output-available"}
              input={call.args}
              output={call.isError ? undefined : call.resultSummary}
              errorText={call.isError ? call.resultSummary : undefined}
            />
          ))}
        </div>
      ) : null}
      {toolCalls.length === 0 &&
      serverRecordedToolNames(iteration.serverTruth).length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-1"
          data-testid="playground-run-server-tools"
          title="The client transcript names no tools; these are the calls Yarn's own logs recorded for this run."
        >
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            tools (server-recorded)
          </span>
          {serverRecordedToolNames(iteration.serverTruth).map((name) => (
            <span
              key={name}
              className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[11px] text-muted-foreground"
            >
              {name}
            </span>
          ))}
        </div>
      ) : null}
      {responseText ? <RevealedResponse text={responseText} /> : null}
      {showBrandIndicator ? (
        <div
          className="text-muted-foreground/80"
          data-testid="playground-run-thinking"
        >
          <LoadingIndicatorContent />
        </div>
      ) : null}
      {error ? (
        <p className="whitespace-pre-wrap rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
      <StatusRow
        text={status.text}
        busy={status.busy}
        showSpinner={!showBrandIndicator}
      >
        {runDetailPath ? (
          <Link
            to={runDetailPath}
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
            data-testid="playground-run-detail-link"
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            Run detail
          </Link>
        ) : null}
      </StatusRow>
    </div>
  );
}
